import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import * as Yup from "yup";

// Validation Schema using Yup
const ipValidationSchema = Yup.object().shape({
  // ip: Yup.string().required("IP is required"),
  // port: Yup.number().required("Port is required").min(1).max(65535),
  // protocol: Yup.string().oneOf(["http", "https"]),
  device_id: Yup.string().required("Device ID is required"),
  name: Yup.string().required("Name is required"),
});

const validationSchema = Yup.object().shape({
  has_machine_ips: Yup.boolean(),
  machine_ips: Yup.array().of(ipValidationSchema),
});

const BusinessSettingsModal = ({ open, onClose, location }) => {
  const [formValues, setFormValues] = useState({
    has_machine_ips: false,
    machine_ips: [{ device_id: "", 
      // ip: "", 
      // port: "", 
      // protocol: "http", 
      name: "", user_type: "Members", attendance_type: "Both" }],
  });
  const [formErrors, setFormErrors] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const getLocationSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}locationSettings/getByLocationId/${location._id}`
      );
      const res = await response.json();
      if (response.status === 200) {
        setFormValues(res || {
          has_machine_ips: false,
          machine_ips: [{ device_id: "", 
            // ip: "", 
            // port: "", 
            // protocol: "http", 
            name: "", user_type: "Members", attendance_type: "Both" }],
        });
        if (res) {
          setIsEditMode(true);
        } else {
          setIsEditMode(false);
        }
      } else {
        console.error("Error fetching location settings:", res.message);
      }
    } catch (error) {
      console.error("Error fetching location settings:", error);
      setFormValues({
        has_machine_ips: false,
        machine_ips: [{ ip: "", port: "", protocol: "http", name: "" }],
      });
        setIsEditMode(false);
    }
  };

  useEffect(() => {
    if (open && location) getLocationSettings();
  }, [open, location]);

  const handleChange = (index, field, value) => {
    const updated = [...formValues.machine_ips];
    updated[index][field] = value;
    setFormValues((prev) => ({ ...prev, machine_ips: updated }));
  };

  const handleAddIP = () => {
    setFormValues((prev) => ({
      ...prev,
      machine_ips: [...prev.machine_ips, { ip: "", port: "", protocol: "http", name: "" }],
    }));
  };

  const handleRemoveIP = (index) => {
    const updated = [...formValues.machine_ips];
    updated.splice(index, 1);
    setFormValues((prev) => ({ ...prev, machine_ips: updated }));
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formValues, { abortEarly: false });
      setFormErrors({});
      setButtonLoading(true);
      const payload = {
        location_id: location._id,
        ...formValues,
      };
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}locationSettings/`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        console.log("Updated successfully", data);
        onClose();
      } else {
        console.error("Error updating:", data.message);
      }
    } catch (err) {
      if (err.name === "ValidationError") {
        const errors = {};
        err.inner.forEach((e) => {
          if (e.path.startsWith("machine_ips")) {
            const match = e.path.match(/machine_ips\[(\d+)\]\.(\w+)/);
            if (match) {
              const [, index, field] = match;
              errors[index] = {
                ...(errors[index] || {}),
                [field]: e.message,
              };
            }
          } else {
            errors[e.path] = e.message;
          }
        });
        setFormErrors(errors);
      } else {
        console.error("Submission error:", err);
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleClose = () => {
    setFormValues({
      has_machine_ips: false,
      machine_ips: [
        {
          device_id: "",
          // ip: "",
          // port: "",
          // protocol: "http",
          name: "",
          user_type: "Members",
          attendance_type: "Both",
        },
      ],
    });
    setFormErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" sx={{ maxHeight: "70vh", overflowY: "auto" }} scroll="paper">
      <DialogTitle>Update Business Settings</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formValues.has_machine_ips}
              onChange={(e) => {
                setFormValues({ ...formValues, has_machine_ips: e.target.checked })
                if(e.target.checked) {
                  setFormValues((prev) => ({
                    ...prev,
                    machine_ips: [{ ip: "", port: "", protocol: "http", name: "", user_type: "Members", attendance_type: "Both" }],
                  }));
                }
              }}
            />
          }
          label="Enable Machine IPs : "
          labelPlacement="start"
        />
          </Grid>
        <Grid item xs={12}>
        {formValues.has_machine_ips &&
          formValues.machine_ips.map((ip, index) => (
            <Card key={index} variant="outlined" sx={{ my: 2 }}>
              <CardContent>
                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                  {/* <Grid item xs={12} md={2}>
                    <TextField
                      label="IP Address"
                      value={ip.ip}
                      onChange={(e) => handleChange(index, "ip", e.target.value)}
                      fullWidth
                      error={!!formErrors[index]?.ip}
                      helperText={formErrors[index]?.ip}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Port"
                      type="number"
                      value={ip.port}
                      onChange={(e) => handleChange(index, "port", e.target.value)}
                      fullWidth
                      error={!!formErrors[index]?.port}
                      helperText={formErrors[index]?.port}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField
                      label="Protocol"
                      select
                      SelectProps={{ native: true }}
                      value={ip.protocol}
                      onChange={(e) => handleChange(index, "protocol", e.target.value)}
                      fullWidth
                    >
                      <option value="http">http</option>
                      <option value="https">https</option>
                    </TextField>
                  </Grid> */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Device ID"
                      value={ip.device_id}
                      onChange={(e) => handleChange(index, "device_id", e.target.value)}
                      fullWidth
                      error={!!formErrors[index]?.device_id}
                      helperText={formErrors[index]?.device_id}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="User Type"
                      select
                      SelectProps={{ native: true }}
                      value={ip.user_type}
                      onChange={(e) => handleChange(index, "user_type", e.target.value)}
                      fullWidth
                    >
                      <option value="Members">Members</option>
                      <option value="Employee">Employee</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Attendance Type"
                      select
                      SelectProps={{ native: true }}
                      value={ip.attendance_type}
                      onChange={(e) => handleChange(index, "attendance_type", e.target.value)}
                      fullWidth
                    >
                      <option value="Both">Both</option>
                      <option value="CheckIn">Check In</option>
                      <option value="CheckOut">Check Out</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Name"
                      value={ip.name}
                      onChange={(e) => handleChange(index, "name", e.target.value)}
                      fullWidth
                      error={!!formErrors[index]?.name}
                      helperText={formErrors[index]?.name}
                    />
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton color="error" onClick={() => handleRemoveIP(index)}>
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            {formValues.has_machine_ips && (
              <Button startIcon={<Add />} onClick={handleAddIP} variant="outlined" fullWidth>
                Add Machine IP
              </Button>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={buttonLoading}>
          {buttonLoading && <CircularProgress size={24} sx={{ mr: 1 }} />}
          {buttonLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessSettingsModal;
